import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import ColorSF2 from '../../styles/common/ColorSF2';
import ExpandMore from '../Icons/ExpandMore';
import ExpandLess from '../Icons/ExpandLess';
import AppSlider from '../Parts/Slider';

const Header = styled.div`
  height: 48px;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${ColorSF2.paleBlue};
  @media (max-height: 470px) {
    height: 30px;
  }
  z-index: 1;
`;
const HeaderLeft = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;
const DeviceInfoWrapper = styled.div`
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (max-height: 470px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex: 1;
    font-size: 14px;
  }
`;
const DeviceInfo = styled.div`
  max-width: 600px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  background-color: ${ColorSF2.paleBlue};
  font-size: 12px;
`;
const ExpiredTime = styled.div`
  min-width: 100px;
  background-color: ${ColorSF2.paleBlue};
  font-size: 10px;
`;
const SliderWrapper = styled.div`
  width: 150px;
  padding: 0 10px;
  margin: 0 10px;
`;
const MembersButton = styled.div`
  width: 155px;
  height: 32px;
  padding: 0px 10px;
  border: 1px solid ${ColorSF2.navyBlue};
  background-color: ${ColorSF2.white};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

/**
 * 遠隔支援画面におけるヘッダーコンポーネント
 *
 * @param {Object} props - このコンポーネントに渡されるプロパティ
 * @param {string} props.currentSharerName - 現在のビデオ共有を行っているユーザー名
 * @param {string} props.deviceName - 共有されているデバイス名
 * @param {string} props.roomCreatedAt - 通話ルームが作成された日時
 * @param {boolean} props.sharerJoined - ビデオ共有ユーザーが通話ルームに参加しているかどうか
 * @param {string} props.expirationDate - 通話ルームの接続有効期限
 * @param {number} props.expansionValue - 画面拡大レベルの値
 * @param {Function} props.setExpansionValue - 画面拡大レベルを設定するコールバック関数
 * @param {Array} props.roomMembers - 通話ルームに参加しているメンバーのリスト
 * @param {number} props.memberCount - 通話ルームに参加しているメンバー数
 * @param {boolean} props.openMembersDialog - MemberDialogが開いているかどうか
 * @param {Function} props.setOpenMembersDialog - openMemberDialogの状態を切り替えるコールバック関数
 * @returns {ReactElement}
 */
const RemoteHeader = ({
  currentSharerName,
  deviceName,
  roomCreatedAt,
  sharerJoined,
  expirationDate,
  expansionValue,
  setExpansionValue,
  roomMembers,
  memberCount,
  openMembersDialog,
  setOpenMembersDialog,
}) => (
  <Header>
    <HeaderLeft>
      {currentSharerName}
      <DeviceInfoWrapper>
        <DeviceInfo>{deviceName}</DeviceInfo>
        {roomCreatedAt && <ExpiredTime>{expirationDate}</ExpiredTime>}
      </DeviceInfoWrapper>
    </HeaderLeft>
    <HeaderRight>
      {sharerJoined && (
        <SliderWrapper>
          <AppSlider
            aria-label="Volume"
            defaultValue={expansionValue}
            min={1}
            max={2}
            step={0.1}
            onChange={(event, newValue) => setExpansionValue(newValue)}
          />
        </SliderWrapper>
      )}
      {roomMembers.length > 0 && (
        <MembersButton
          className="button"
          onClick={() => {
            setOpenMembersDialog(prevOpen => !prevOpen);
          }}
        >
          <FormattedMessage
            id="remotePage.button.members"
            values={{ count: memberCount }}
          />
          {!openMembersDialog && (
            <ExpandMore color={ColorSF2.navyBlue} width="18px" height="18px" />
          )}
          {openMembersDialog && (
            <ExpandLess color={ColorSF2.navyBlue} width="18px" height="18px" />
          )}
        </MembersButton>
      )}
    </HeaderRight>
  </Header>
);

RemoteHeader.propTypes = {
  currentSharerName: PropTypes.string.isRequired,
  deviceName: PropTypes.string.isRequired,
  roomCreatedAt: PropTypes.string.isRequired,
  sharerJoined: PropTypes.bool.isRequired,
  expirationDate: PropTypes.string.isRequired,
  expansionValue: PropTypes.number.isRequired,
  setExpansionValue: PropTypes.func.isRequired,
  roomMembers: PropTypes.array.isRequired,
  memberCount: PropTypes.number.isRequired,
  openMembersDialog: PropTypes.bool.isRequired,
  setOpenMembersDialog: PropTypes.func.isRequired,
};

export default RemoteHeader;
